import {
    BuildingData,
    Metadata,
    RemoteConstantsModel,
    ResourceData,
    TechTreeComponent,
    TileData,
    UnitData,
} from "../models/remoteConstants.model";
import {RecordSectionKey, SectionKey} from "./branchDiff";

// Whole-record migration between branches (the Compare Branches tool).
// Metadata sections share one record shape (uid+id+values+entities+requires);
// entity sections each have their own, but the migration mechanics are the
// same everywhere: a record, its localization rows, its image pack and any
// image entries Current lacks travel together as one package, applied (and
// undone) as one history entry.
//
// Records are matched across branches by IDENTITY: metadata records by their
// deterministic namespaced uid, entity/techTree records by id.

const METADATA_SECTION_KEYS: SectionKey[] = [
    'unitAttributes', 'buildingAttributes',
    'techActionAttributes', 'techPassiveAttributes',
    'rewards',
];

export function isMetadataSection(sectionKey: SectionKey): boolean {
    return METADATA_SECTION_KEYS.includes(sectionKey);
}

export type MigratableRecord =
    Metadata | UnitData | BuildingData | TileData | ResourceData | TechTreeComponent;

export function identityOf(record: MigratableRecord, sectionKey: SectionKey): string {
    return isMetadataSection(sectionKey) ? (record as Metadata).uid : record.id;
}

export interface RecordMigration {
    sectionKey: SectionKey;
    // The record's match key on Current: uid for metadata, id for entities.
    identity: string;
    record: MigratableRecord;
    // localization[lang][key] entries to copy from Other into Current. Only
    // entries actually present in Other are included.
    locRows: { lang: string; key: string; value: string }[];
    // The pack row at imagePacks[identity], if present in Other.
    packRow?: string[][];
    // images[key] entries that need to come over (not already in Current).
    imageRows: { key: string; value: unknown }[];
}

export interface MigrationClassification {
    resolvable: boolean;
    migration?: RecordMigration;
    // Human-readable list of records the user has to bring over (or
    // create) on Current first. Suitable for a tooltip.
    missingDeps?: string[];
    // Non-blocking caveats — the migration will apply, but the user should
    // know about these (e.g. a tech node arriving unattached).
    notes?: string[];
}

// Returns the section's records, read-only. Commands use
// ensureSectionCollection instead (which initializes missing arrays).
export function getSectionRecords(state: RemoteConstantsModel, sectionKey: SectionKey): MigratableRecord[] {
    switch (sectionKey) {
        case 'unitAttributes':        return state.unitAttributes ?? [];
        case 'buildingAttributes':    return state.buildingAttributes ?? [];
        case 'techActionAttributes':  return state.techActionAttributes ?? [];
        case 'techPassiveAttributes': return state.techPassiveAttributes ?? [];
        case 'rewards':               return state.rewards ?? [];
        case 'units':                 return state.units ?? [];
        case 'buildings':             return state.buildings ?? [];
        case 'tiles':                 return state.tiles ?? [];
        case 'resources':             return state.resources ?? [];
        case 'techTree':              return state.techTree ?? [];
        default:                      return [];
    }
}

export function findRecordByIdentity(
    state: RemoteConstantsModel,
    sectionKey: SectionKey,
    identity: string,
): MigratableRecord | undefined {
    return getSectionRecords(state, sectionKey).find(r => identityOf(r, sectionKey) === identity);
}

function collectEntityIds(state: RemoteConstantsModel): Set<string> {
    const s = new Set<string>();
    state.units?.forEach(u => s.add(u.id));
    state.buildings?.forEach(b => s.add(b.id));
    state.tiles?.forEach(t => s.add(t.id));
    state.resources?.forEach(r => s.add(r.id));
    state.techTree?.forEach(n => s.add(n.id));
    return s;
}

// Every uid AND id across the five metadata collections. Entity `attributes`
// arrays and tech-node `unlocks` reference metadata records — by uid in
// migrated data, by id in legacy data — and the client resolves uid-then-id,
// so a reference that hits either is satisfiable. Namespaced uids make
// cross-collection collisions implausible, so one combined universe is fine.
function collectMetadataRefs(state: RemoteConstantsModel): Set<string> {
    const s = new Set<string>();
    const colls = [
        state.unitAttributes, state.buildingAttributes,
        state.techActionAttributes, state.techPassiveAttributes,
        state.rewards,
    ];
    for (const coll of colls) coll?.forEach(m => { s.add(m.uid); s.add(m.id); });
    return s;
}

// ---------------------------------------------------------------------------
// Dependency checks — everything the record references must resolve on
// Current (post-migration) or the record arrives broken.
// ---------------------------------------------------------------------------

function collectMetadataMissingDeps(
    record: Metadata,
    sectionKey: SectionKey,
    targetState: RemoteConstantsModel,
    currentState: RemoteConstantsModel,
    resolveName: (id: string) => string,
): string[] {
    const missingDeps: string[] = [];
    const currentMeta = getSectionRecords(currentState, sectionKey) as Metadata[];
    const currentEntityIds = collectEntityIds(currentState);

    // `requires`: each entry is an attribute id (same-section). Current
    // must have at least one record with that id in the same collection
    // for the requirement to be satisfiable post-migration.
    if (record.requires && record.requires.length > 0) {
        const currentIds = new Set(currentMeta.map(m => m.id));
        for (const req of record.requires) {
            if (!currentIds.has(req)) {
                missingDeps.push(`Required attribute "${req}" not present in Current.`);
            }
        }
    }

    // `entities.<slot> = entityUid` — each referenced entity must exist on
    // Current. We accept any of units/buildings/tiles/resources/techTree
    // (the slot semantics depend on the attribute's runtime code).
    for (const [slot, entityUid] of Object.entries(record.entities ?? {})) {
        if (!entityUid) continue;
        if (!currentEntityIds.has(entityUid)) {
            missingDeps.push(`Entity for slot "${slot}" (${resolveName(entityUid)}) not present in Current.`);
        }
    }

    // `values.<entityUid> = N` — EntityValue params use an entity uid as
    // the key. Distinguish heuristically: if a values-key looks like a uid
    // we recognise (in either branch), check it. Otherwise it's a named
    // parameter (e.g. "attackBonus") and no check needed.
    const knownInTarget = collectEntityIds(targetState);
    for (const key of Object.keys(record.values ?? {})) {
        if (!knownInTarget.has(key)) continue;       // not a uid-typed key
        if (!currentEntityIds.has(key)) {
            missingDeps.push(`Parameter for entity (${resolveName(key)}) not present in Current.`);
        }
    }

    return missingDeps;
}

function collectEntityMissingDeps(
    record: MigratableRecord,
    sectionKey: SectionKey,
    currentState: RemoteConstantsModel,
    resolveName: (id: string) => string,
): string[] {
    const missingDeps: string[] = [];
    const metaRefs = collectMetadataRefs(currentState);
    const entityIds = collectEntityIds(currentState);

    const checkAttributes = (attributes: string[] | undefined) => {
        for (const a of attributes ?? []) {
            if (!metaRefs.has(a)) {
                missingDeps.push(`Attribute "${resolveName(a)}" (${a}) not present in Current.`);
            }
        }
    };

    switch (sectionKey) {
        case 'units': {
            checkAttributes((record as UnitData).attributes);
            break;
        }
        case 'buildings': {
            const b = record as BuildingData;
            checkAttributes(b.attributes);
            const unitIds = new Set((currentState.units ?? []).map(u => u.id));
            for (const u of b.produceableUnits ?? []) {
                if (!unitIds.has(u)) {
                    missingDeps.push(`Produceable unit "${resolveName(u)}" not present in Current.`);
                }
            }
            for (const tier of b.rewards ?? []) {
                for (const rewardUid of tier ?? []) {
                    if (!metaRefs.has(rewardUid)) {
                        missingDeps.push(`Reward "${resolveName(rewardUid)}" (${rewardUid}) not present in Current.`);
                    }
                }
            }
            for (const pr of b.passiveRewards ?? []) {
                const t = pr.trigger as { adjacentTo?: string; containing?: string };
                for (const ref of [t.adjacentTo, t.containing]) {
                    if (ref && !entityIds.has(ref)) {
                        missingDeps.push(`Passive reward trigger entity "${resolveName(ref)}" not present in Current.`);
                    }
                }
            }
            break;
        }
        case 'tiles': {
            const t = record as TileData;
            const tileIds = new Set((currentState.tiles ?? []).map(x => x.id));
            const resourceIds = new Set((currentState.resources ?? []).map(x => x.id));
            const buildingIds = new Set((currentState.buildings ?? []).map(x => x.id));
            if (t.supportingTile && !tileIds.has(t.supportingTile)) {
                missingDeps.push(`Supporting tile "${resolveName(t.supportingTile)}" not present in Current.`);
            }
            for (const r of t.resources ?? []) {
                if (!resourceIds.has(r)) {
                    missingDeps.push(`Resource "${resolveName(r)}" not present in Current.`);
                }
            }
            for (const b of t.buildings ?? []) {
                if (!buildingIds.has(b)) {
                    missingDeps.push(`Building "${resolveName(b)}" not present in Current.`);
                }
            }
            break;
        }
        case 'resources': {
            checkAttributes((record as ResourceData).attributes);
            break;
        }
        case 'techTree': {
            const n = record as TechTreeComponent;
            const nodeIds = new Set((currentState.techTree ?? []).map(x => x.id));
            for (const child of n.children ?? []) {
                if (!nodeIds.has(child)) {
                    missingDeps.push(`Child tech node "${resolveName(child)}" not present in Current.`);
                }
            }
            // Unlocks reference entities (by id) or metadata (by uid/id).
            for (const unlock of n.unlocks ?? []) {
                if (!entityIds.has(unlock) && !metaRefs.has(unlock)) {
                    missingDeps.push(`Unlock "${resolveName(unlock)}" (${unlock}) not present in Current.`);
                }
            }
            break;
        }
    }

    return missingDeps;
}

function collectMissingDeps(
    record: MigratableRecord,
    sectionKey: SectionKey,
    targetState: RemoteConstantsModel,
    currentState: RemoteConstantsModel,
    resolveName: (id: string) => string,
): string[] {
    return isMetadataSection(sectionKey)
        ? collectMetadataMissingDeps(record as Metadata, sectionKey, targetState, currentState, resolveName)
        : collectEntityMissingDeps(record, sectionKey, currentState, resolveName);
}

// ---------------------------------------------------------------------------
// Satellite collection — localization rows, icon/image pack, image entries.
// ---------------------------------------------------------------------------

// The localization keys a record owns, by convention:
//   metadata:  <uid> (name) + <id>.description (shared description)
//   entities:  <id> (name) + <id>.description
//   buildings: additionally each passive reward's <uid> / <uid>.description
//              (per-instance descriptions — layered design, keys may exist)
function ownedLocalizationKeys(record: MigratableRecord, sectionKey: SectionKey): string[] {
    if (isMetadataSection(sectionKey)) {
        const m = record as Metadata;
        return [m.uid, `${m.id}.description`];
    }
    const keys = [record.id, `${record.id}.description`];
    if (sectionKey === 'buildings') {
        for (const pr of (record as BuildingData).passiveRewards ?? []) {
            if (pr.uid) keys.push(pr.uid, `${pr.uid}.description`);
        }
    }
    return keys;
}

export function buildMigrationPackage(
    record: MigratableRecord,
    sectionKey: SectionKey,
    targetState: RemoteConstantsModel,
    currentState: RemoteConstantsModel,
): RecordMigration {
    const identity = identityOf(record, sectionKey);

    const locRows: RecordMigration['locRows'] = [];
    const langs = Object.keys(targetState.localization ?? {});
    const wantedKeys = ownedLocalizationKeys(record, sectionKey);
    for (const lang of langs) {
        const table = targetState.localization?.[lang] ?? {};
        for (const k of wantedKeys) {
            if (k in table) locRows.push({lang, key: k, value: table[k]});
        }
    }

    // Image pack at the identity key: a multi-level entity pack, or a
    // single-slot icon indirection for metadata. Pack-referenced image
    // entries come along if Current lacks them; image FILES never need
    // migrating — all branches share one blob container, so copying the
    // fileName-bearing entry suffices.
    const packRow = targetState.imagePacks?.[identity];
    const referenced = new Set<string>();
    if (packRow) {
        for (const variations of packRow) for (const key of variations) referenced.add(key);
    }
    // (The "entity image" convention is retired: an entity's image is its pack
    // top-left slot, which is already in `referenced` above. A bare images[id]
    // the pack no longer references is a drift orphan and is intentionally not
    // carried over.)

    const imageRows: RecordMigration['imageRows'] = [];
    referenced.forEach(key => {
        if (!targetState.images?.[key]) return;
        // Only include images Current doesn't already have — never
        // overwrite an image asset Current may share with other things.
        if (currentState.images?.[key] !== undefined) return;
        imageRows.push({key, value: targetState.images[key]});
    });

    return {sectionKey, identity, record, locRows, packRow, imageRows};
}

// ---------------------------------------------------------------------------
// Classifiers
// ---------------------------------------------------------------------------

// ADD: bring a record that exists only on Other into Current.
export function classifyRecordMigration(
    targetRecord: MigratableRecord,
    sectionKey: SectionKey,
    targetState: RemoteConstantsModel,
    currentState: RemoteConstantsModel,
    resolveName: (id: string) => string,
    currentBranchLabel: string,
): MigrationClassification {
    const identity = identityOf(targetRecord, sectionKey);

    // "Same identity on both sides" is the SAME logical record — that's the
    // Update flow (Differs list), not an Add.
    if (findRecordByIdentity(currentState, sectionKey, identity)) {
        return {
            resolvable: false,
            missingDeps: [`${identity} already exists on current (${currentBranchLabel}) — sync it from the Differs list instead.`],
        };
    }

    const missingDeps = collectMissingDeps(targetRecord, sectionKey, targetState, currentState, resolveName);
    if (missingDeps.length > 0) {
        return {resolvable: false, missingDeps};
    }

    const notes: string[] = [];
    if (sectionKey === 'techTree') {
        // Parentage lives in the PARENT's children array — an added node is
        // unattached until some node on Current lists it.
        const referencedAsChild = (currentState.techTree ?? [])
            .some(n => (n.children ?? []).includes(identity));
        if (!referencedAsChild) {
            notes.push(`No tech node on Current lists "${identity}" as a child — it will be unattached until you sync its parent.`);
        }
    }

    return {
        resolvable: true,
        migration: buildMigrationPackage(targetRecord, sectionKey, targetState, currentState),
        notes: notes.length > 0 ? notes : undefined,
    };
}

// ---------------------------------------------------------------------------
// REMOVAL: propagate a deletion Other already made (record exists only on
// Current). Refused while anything on Current still references the record, so
// removing it can't leave dangling references behind.
// ---------------------------------------------------------------------------

export interface RemovalClassification {
    removable: boolean;
    // When not removable, the records on Current that still reference this one.
    blockers?: string[];
}

// Every place a record identity (entity id, metadata uid/id, or tech-node id)
// can be referenced from. Conservative: any string match counts as a reference,
// which can only ever make removal MORE cautious, never less.
export function collectReferencesTo(
    identity: string,
    state: RemoteConstantsModel,
    resolveName: (id: string) => string,
): string[] {
    const blockers: string[] = [];
    const hit = (where: string) => blockers.push(where);

    for (const u of state.units ?? []) {
        if ((u.attributes ?? []).includes(identity)) hit(`Unit "${resolveName(u.id)}" attribute`);
    }
    for (const b of state.buildings ?? []) {
        if ((b.attributes ?? []).includes(identity)) hit(`Building "${resolveName(b.id)}" attribute`);
        if ((b.produceableUnits ?? []).includes(identity)) hit(`Building "${resolveName(b.id)}" produceable unit`);
        for (const tier of b.rewards ?? []) {
            if ((tier ?? []).includes(identity)) hit(`Building "${resolveName(b.id)}" reward`);
        }
        for (const pr of b.passiveRewards ?? []) {
            const t = pr.trigger as { adjacentTo?: string; containing?: string };
            if (t.adjacentTo === identity || t.containing === identity) hit(`Building "${resolveName(b.id)}" passive reward trigger`);
        }
    }
    for (const t of state.tiles ?? []) {
        if (t.supportingTile === identity) hit(`Tile "${resolveName(t.id)}" supporting tile`);
        if ((t.resources ?? []).includes(identity)) hit(`Tile "${resolveName(t.id)}" resource`);
        if ((t.buildings ?? []).includes(identity)) hit(`Tile "${resolveName(t.id)}" building`);
    }
    for (const r of state.resources ?? []) {
        if ((r.attributes ?? []).includes(identity)) hit(`Resource "${resolveName(r.id)}" attribute`);
    }
    for (const n of state.techTree ?? []) {
        if ((n.children ?? []).includes(identity)) hit(`Tech node "${resolveName(n.id)}" child`);
        if ((n.unlocks ?? []).includes(identity)) hit(`Tech node "${resolveName(n.id)}" unlock`);
    }
    const metaColls = [
        state.unitAttributes, state.buildingAttributes,
        state.techActionAttributes, state.techPassiveAttributes, state.rewards,
    ];
    for (const coll of metaColls) {
        for (const m of coll ?? []) {
            if ((m.requires ?? []).includes(identity)) hit(`Attribute "${resolveName(m.uid)}" requirement`);
            for (const slot of Object.values(m.entities ?? {})) {
                if (slot === identity) hit(`Attribute "${resolveName(m.uid)}" entity slot`);
            }
            if (m.values && identity in m.values) hit(`Attribute "${resolveName(m.uid)}" parameter`);
        }
    }
    return blockers;
}

export function classifyRecordRemoval(
    record: MigratableRecord,
    sectionKey: RecordSectionKey,
    currentState: RemoteConstantsModel,
    resolveName: (id: string) => string,
): RemovalClassification {
    const identity = identityOf(record, sectionKey);
    // For metadata, references in the data point at either the uid or the id;
    // check both so an id-keyed legacy reference still blocks removal.
    const candidates = isMetadataSection(sectionKey)
        ? Array.from(new Set([identity, (record as Metadata).id]))
        : [identity];

    const blockers = new Set<string>();
    for (const c of candidates) {
        for (const b of collectReferencesTo(c, currentState, resolveName)) blockers.add(b);
    }

    return blockers.size > 0
        ? {removable: false, blockers: Array.from(blockers)}
        : {removable: true};
}

// UPDATE: sync Current's same-identity record (fields + localization +
// image pack) to match Other's version.
export function classifyRecordUpdate(
    targetRecord: MigratableRecord,
    sectionKey: SectionKey,
    targetState: RemoteConstantsModel,
    currentState: RemoteConstantsModel,
    resolveName: (id: string) => string,
): MigrationClassification {
    const identity = identityOf(targetRecord, sectionKey);

    const currentRecord = findRecordByIdentity(currentState, sectionKey, identity);
    if (!currentRecord) {
        return {
            resolvable: false,
            missingDeps: [`${identity} does not exist on Current — use the migrate (add) flow instead.`],
        };
    }

    const missingDeps = collectMissingDeps(targetRecord, sectionKey, targetState, currentState, resolveName);
    if (missingDeps.length > 0) {
        return {resolvable: false, missingDeps};
    }

    const notes: string[] = [];
    if (sectionKey === 'techTree') {
        // Syncing a node's children to Other's version can detach subtrees
        // that only Current had hanging off this node.
        const targetChildren = new Set(((targetRecord as TechTreeComponent).children ?? []));
        const removed = ((currentRecord as TechTreeComponent).children ?? [])
            .filter(c => !targetChildren.has(c));
        for (const c of removed) {
            notes.push(`Sync removes child "${resolveName(c)}" — that node (and its subtree) will become unattached.`);
        }
    }

    return {
        resolvable: true,
        migration: buildMigrationPackage(targetRecord, sectionKey, targetState, currentState),
        notes: notes.length > 0 ? notes : undefined,
    };
}
